import { useState } from 'react';
import { Button } from '../common/Button';
import {
  useClaudeCodeIngestTokenStatus,
  useRotateClaudeCodeIngestToken,
  useUnattributedClaudeCodeUsage,
} from '../../hooks/useBotUsage';
import { formatDisplayDateTime } from '../../utils/format';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

/**
 * MKS-298 — the Usage tab's Claude Code OpenTelemetry panel: the state of the
 * OTLP ingest token (with rotation) and the usage that the per-connection
 * table below has no row for.
 *
 * `UsageTab.tsx` owns where this panel sits (above the table in every branch),
 * so this component itself only renders the panel.
 *
 * The token plaintext appears exactly once, in the rotate response, and lives
 * only in this component's state until the admin navigates away. Nothing
 * caches it and no read endpoint can return it.
 */

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function settingsPayload(endpointBase: string): string {
  return JSON.stringify(
    {
      env: {
        CLAUDE_CODE_ENABLE_TELEMETRY: '1',
        OTEL_METRICS_EXPORTER: 'otlp',
        OTEL_LOGS_EXPORTER: 'none',
        OTEL_EXPORTER_OTLP_PROTOCOL: 'http/json',
        OTEL_EXPORTER_OTLP_ENDPOINT: endpointBase,
        OTEL_EXPORTER_OTLP_HEADERS: 'Authorization=Bearer <ingest token>',
        OTEL_EXPORTER_OTLP_METRICS_TEMPORALITY_PREFERENCE: 'delta',
      },
    },
    null,
    2,
  );
}

export function ClaudeCodeIngestPanel() {
  const { data: tokenStatus, isLoading: tokenLoading } = useClaudeCodeIngestTokenStatus();
  const { data: unattributed } = useUnattributedClaudeCodeUsage();
  const rotate = useRotateClaudeCodeIngestToken();
  const [freshToken, setFreshToken] = useState<string | null>(null);

  // The receiver lives on this same origin; the exporter appends
  // `/v1/metrics` to the base, so the base is what the admin pastes.
  const endpointBase = `${window.location.origin}/api/otel`;

  const onRotate = async () => {
    if (
      !window.confirm(
        'Generate a new ingest token? Every Claude Code session stops reporting until the new token is pasted into claude.ai managed settings.',
      )
    ) {
      return;
    }
    const result = await rotate.mutateAsync();
    setFreshToken(result.token);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Claude Code usage export</h3>
        <p className="text-sm text-gray-500 mt-1">
          The cost and token columns below count every Claude Code session — own machines, Factory™
          runs and Monka — via Claude Code&apos;s OpenTelemetry metrics, delivered org-wide from
          claude.ai → Organization settings → Claude Code → Managed settings. See
          docs/private/CLAUDE-CODE-OTEL.md for the roll-out.
        </p>
      </div>

      <div className="rounded-lg bg-gray-50 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase">Ingest token</p>
        {tokenLoading ? (
          <p className="text-sm text-gray-500 mt-2">Loading...</p>
        ) : (
          <div className="mt-2 space-y-2">
            <p className="text-sm text-gray-700">
              {tokenStatus?.configured ? (
                <>
                  Active, ending <span className="font-mono">…{tokenStatus.hint}</span>
                  {tokenStatus.rotatedAt
                    ? ` — rotated ${formatDisplayDateTime(tokenStatus.rotatedAt)}`
                    : ''}
                </>
              ) : (
                'No token yet — the receiver accepts nothing until one is generated.'
              )}
            </p>
            <p className="text-sm text-gray-700">
              Accepted organization id:{' '}
              <span className="font-mono">{tokenStatus?.organizationId ?? 'any (not set)'}</span>
            </p>
            <Button variant="secondary" size="sm" onClick={onRotate} disabled={rotate.isPending}>
              {rotate.isPending
                ? 'Generating...'
                : tokenStatus?.configured
                  ? 'Rotate token'
                  : 'Generate token'}
            </Button>
            {rotate.isError && (
              <p className="text-sm text-red-600">{(rotate.error as Error).message}</p>
            )}
          </div>
        )}

        {freshToken && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-800">
              Copy this now — it is shown once and cannot be read back.
            </p>
            <p className="mt-1 font-mono text-xs break-all text-amber-900">{freshToken}</p>
          </div>
        )}

        <details className="mt-3">
          <summary className="text-sm text-gray-600 cursor-pointer">
            Managed settings payload to paste
          </summary>
          <pre className="mt-2 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
            {settingsPayload(endpointBase)}
          </pre>
        </details>
      </div>

      {unattributed && unattributed.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase">Not in the table below</p>
          <p className="text-sm text-gray-500 mt-1">
            Reported usage with no row above: an email no Monksheets user owns, or a person with no
            bot connection. Never dropped — listed here instead.
          </p>
          <table className="min-w-full divide-y divide-gray-200 mt-2">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Reported as
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Why
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  30d $
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  30d tokens
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Last report
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {unattributed.map((row) => (
                // Keyed on subject AND user: a subject whose rows were written
                // before that person existed in Monksheets can appear twice,
                // once unattributed and once adopted.
                <tr key={`${row.subjectKey}:${row.userId ?? 'none'}`}>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {row.displayName ?? row.subjectKey}
                    {row.displayName && (
                      <span className="block text-xs text-gray-400">{row.subjectKey}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {row.reason === 'unmatched_email' ? 'No Monksheets user' : 'No bot connection'}
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">
                    {formatUsd(row.costUsd30d)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">
                    {formatNumber(row.tokens30d, undefined, LEGACY_EN_NUMBER_LOCALE)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {formatDisplayDateTime(row.lastReportAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
